<template>
  <b-container
    class="accordion-list p-0"
    fluid
  >
    <accordion-panel
      v-for="(module, index) in modules"
      header-class="accordion-list__module-accordion"
      :key="index"
      :id="module.moduleId"
      :collapsed.sync="module.collapsed"
    >
      <template #title>
        <b-row>
          <b-col cols="9">
            <b-checkbox
              v-if="showCheckboxes"
              class="d-inline"
              :checked="isAllAlarmsFromModuleSelected(module)"
              @change="markAllAlarmsFromModules($event, module)"
            >
              <span class="accordion-list__module-title ml-3">
                <strong>{{ module.moduleName }}</strong>
              </span>
            </b-checkbox>
            <span
              v-else
              class="accordion-list__module-title"
            >
              <strong>{{ module.moduleName }}</strong>
            </span>

            <span class="accordion-list__module-exhibition-text float-right pr-3">
              {{ getExhibitionResumeText(module) }}
            </span>
          </b-col>
          <b-col
            class="p-0"
            cols="2"
          >
            <strong> {{ $t('general.classification') }} </strong>
          </b-col>
          <b-col
            v-if="hasExhibitionPermission"
            class="p-0"
            cols="1"
          >
            <strong> {{ $t('general.exhibition') }} </strong>
          </b-col>
        </b-row>
      </template>
      <div>
        <b-row
          v-for="alarm in module.alarms"
          class="accordion-list__alarms-row"
          :key="alarm.id"
        >
          <b-col cols="9">
            <b-checkbox
              v-if="showCheckboxes"
              class="accordion-list__alarm-checkbox"
              :class="getAlarmDisplayClass(alarm)"
              :checked="isAlarmSelected(alarm.id)"
              @change="markAlarm($event, alarm, module)"
            />
            <span :class="getAlarmDisplayClass(alarm)">
              <i
                class="fas fa-bell mr-1"
                aria-hidden="true"
              />
              {{ alarm.name }}
            </span>
          </b-col>
          <b-col
            class="p-0 accordion-list__alarms-classification-wrapper"
            cols="2"
          >
            <div
              class="accordion-list__classification-badge d-inline-block"
              :style="classificationBadgeStyle"
            >
              <info-badge
                v-if="isChromaDiagnosis(alarm)"
                class="accordion-list__info-badge"
                translation-key="messages.MI020.message"
                container="body"
              />
              <classification
                v-else
                :class="getAlarmDisplayClass(alarm)"
                :classification="getClassification(alarm)"
                @click-link="openAlarmDetailsModal(alarm)"
              />
            </div>
          </b-col>
          <b-col
            v-if="hasExhibitionPermission"
            class="p-0 pl-4"
            cols="1"
          >
            <icon-badge-status-column
              :status-list="alarm.displays"
              :custom-label="getLabelByStatus(alarm.displays)"
              :icon-color="getIconColorByStatus(alarm.displays)"
              :icon-class="getIconClassByStatus(alarm.displays)"
              :outlined="false"
              @click-link="openAlarmDetailsModal(alarm)"
            />
          </b-col>
        </b-row>
      </div>
    </accordion-panel>
    <alarm-details-modal
      v-model="alarmDetails.value"
      :title="alarmDetails.title"
      :alarm-id="alarmDetails.alarmId"
      :filter="filter"
    />
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import { ClassificationDefinitions, ClassificationType } from 'sigma-mappings';
import AccordionPanel from '@/components/AccordionPanel';
import IconBadgeStatusColumn from '@/components/icon/IconBadgeStatusColumn';
import InfoBadge from '@/components/icon/InfoBadge';
import Classification from '@/components/Classification';
import AlarmDetailsModal from './modal/AlarmDetailsModal';

export default {
  name: 'AccordionList',
  props: {
    modules: {
      type: Array,
      default: () => ([]),
    },
    selectedAlarms: {
      type: Array,
      default: () => ([]),
    },
    showCheckboxes: {
      type: Boolean,
      default: true,
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    AccordionPanel,
    IconBadgeStatusColumn,
    AlarmDetailsModal,
    InfoBadge,
    Classification,
  },
  data: () => ({
    alarmDetails: {
      value: false,
      title: '',
      alarmId: '',
    },
  }),
  computed: {
    ...mapGetters([
      'currentFontSize',
    ]),
    selectedAlarmsProxy: {
      get() {
        return this.selectedAlarms;
      },
      set(value) {
        this.$emit('update:selected-alarms', value);
      },
    },
    hasExhibitionPermission() {
      return this.$hasUserPermission('PER005');
    },
    classificationBadgeStyle() {
      const widths = {
        'manage-fonts_14px': '130px',
        'manage-fonts_16px': '150px',
        'manage-fonts_18px': '180px',
      };
      return {
        'max-width': widths[this.currentFontSize],
      };
    },
  },
  methods: {
    getClassification(classification) {
      const classificationsProcessed = classification?.classifications || [];

      if (this.filter.alarmClassification) {
        return this.filter.alarmClassification;
      }

      if (classificationsProcessed.includes(ClassificationType.CRITICAL)) {
        return ClassificationType.CRITICAL;
      }

      if (classificationsProcessed.includes(ClassificationType.URGENT)) {
        return ClassificationType.URGENT;
      }

      if (classificationsProcessed.includes(ClassificationType.ORDINARY)) {
        return ClassificationType.ORDINARY;
      }

      return undefined;
    },
    isChromaDiagnosis({ classificationDefinedBy }) {
      return classificationDefinedBy === ClassificationDefinitions.CHROMA_DIAGNOSIS;
    },
    isAllAlarmsFromModuleSelected(module) {
      return (module.alarms || []).length
        && module.alarms.every(
          item => this.isAlarmSelected(item.id),
        );
    },
    isAlarmSelected(id) {
      return !!this.selectedAlarmsProxy.find(item => item.id === id);
    },
    markAllAlarmsFromModules(state, module) {
      this.$emit('mark-all-from-module', state, module);
    },
    markAlarm(state, alarm, module) {
      this.$emit('mark-alarm', state, alarm, module);
    },
    getAlarmDisplayClass(alarm) {
      const [display] = alarm.displays;
      return {
        'accordion-list__opacity': alarm.classificationDefinedBy === ClassificationDefinitions.CHROMA_DIAGNOSIS,
        'accordion-list__alarms-row--line-through': (alarm.displays || []).length === 1 && !display,
      };
    },
    getLabelByStatus(alarmsList) {
      return `+${(alarmsList || []).length}`;
    },
    getIconColorByStatus(alarmsList) {
      const [alarm] = (alarmsList || []);
      return alarm ? '' : 'var(--color-gray-intermediary)';
    },
    getIconClassByStatus(alarmsList) {
      const [alarm] = (alarmsList || []);
      return alarm ? 'fas fa-eye' : 'fas fa-eye-slash';
    },
    getExhibitionResumeText(module) {
      const showingNumber = (module.alarms || []).length;
      const totalNumber = module.total;
      return this.$t('alarm-configurations.showing-n-of-n-alarms', {
        showingNumber,
        totalNumber,
      });
    },
    openAlarmDetailsModal(alarm) {
      this.alarmDetails = {
        value: true,
        title: alarm.name,
        alarmId: alarm.id,
      };
    },
  },
};
</script>

<style lang="sass" scoped>
  .accordion-list
    &__module-title
      font-size: 1.1rem
    &__classification-badge
      display: flex
      justify-content: space-between
    &__module-exbition-text
      color: var(--color-gray)
    &__opacity
      opacity: .6
    &__alarms-row
      margin: 0
      padding: 8px 8% 8px 0
      &--line-through
        ::v-deep .classification
          background-color: transparent !important
          color: var(--color-gray-intermediary) !important
      &:nth-child(even)
        background-color: var(--color-table-body-accent-bg)
      &--line-through,
      &--line-through ::v-deep .classification-type__label,
      &--line-through ::v-deep .classification-type__label > i,
      &--line-through ::v-deep label
        text-decoration: line-through
        color: var(--color-gray-intermediary) !important
    &__alarm-checkbox
      padding-left: 40px
      display: inline-block
    ::v-deep .accordion-list
      &__module-accordion
        padding: 15px 10px !important
        border-radius: 0
        background-color: var(--color-table-header-bg) !important
</style>
<style lang="sass">
  .accordion-list
    .accordion-panel
      &__header
        border-bottom: 1px solid var(--color-background-dark)
      &__title-container
        width: calc(100% - 60px)
      &__slot-container
        margin: 0 !important
</style>
